/**
 * Karta wykonawcy na liście klienta (makieta „VoltValue dla domu” 12, D8 i D14): jeden kształt danych dla przykładu
 * na stronie `/dla-domu` i dla prawdziwej listy w „Moich zleceniach”.
 *
 * Prawdziwa lista bierze uczestnika przetargu z `ogl.przetarg.uczestnicy` (backend `rynek/przetarg.dla_kowalskiego`)
 * z profilem publicznym wykonawcy z pakietu P2 (`logo_url`, `zdjecia`, `liczba_zdjec`, `specjalizacje`, `certyfikat`,
 * `google`), ofertą z pakietu P0 (`termin_tygodni`, `gwarancja_lat`) i powodami dopasowania z pakietu P1 (`powody`).
 * Czego jeszcze nie ma w odpowiedzi, karta nie pokazuje: bez zdjęć, bez ocen, bez powodów.
 */

const odmiana = (n, jeden, kilka, wiele) => {
  if (n === 1) return jeden
  return n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? kilka : wiele
}

export const lata = (n) => `${n} ${odmiana(n, 'rok', 'lata', 'lat')}`
export const tygodnie = (n) => `do ${n} ${odmiana(n, 'tygodnia', 'tygodni', 'tygodni')}`
export const wykonawcow = (n) => `${n} ${odmiana(n, 'wykonawca', 'wykonawców', 'wykonawców')}`

const zdjecie = (z, i) => (typeof z === 'string'
  ? { url: z, podpis: ['Na dachu', 'W domu'][i] || '' }
  : { url: z?.url, podpis: z?.podpis || '' })

/** Fakty z ankiety wykonawcy (bez ocen): do dwóch na karcie, jak w makiecie. */
const faktyZAnkiety = (f) => [
  f.od_roku && `Działa od ${f.od_roku}`,
  f.gwarancja_lat != null && `Gwarancja na montaż ${lata(f.gwarancja_lat)}`,
  f.wlasna_ekipa && 'Własna ekipa',
  f.formalnosci && 'Pomaga z formalnościami',
  f.reakcja_awaria_h && `Reakcja na awarię do ${f.reakcja_awaria_h} h`,
].filter(Boolean)

/** Uczestnik przetargu z API na kartę. */
export const kartaZUczestnika = (u) => {
  const f = u.firma || {}
  const o = u.oferta || {}
  const zdjecia = (f.zdjecia || []).slice(0, 2).map(zdjecie).filter((z) => z.url)
  const wszystkich = f.liczba_zdjec ?? (f.zdjecia || []).length
  return {
    id: u.id,
    nazwa: f.nazwa,
    logo: f.logo_url || null,
    certyfikat: Boolean(f.certyfikat),
    miasto: f.miasto || '',
    odleglosc: u.odleglosc_km != null ? `ok. ${Math.round(u.odleglosc_km)} km` : '',
    google: f.google?.ocena ? f.google : null,
    zdjecia,
    wiecej: Math.max(0, wszystkich - zdjecia.length),
    powody: u.powody || [],
    specjalizacje: f.specjalizacje || [],
    fakty: faktyZAnkiety(f).slice(0, 2),
    cena: o.cena_brutto ?? null,
    termin: o.termin_od ? o.termin_montazu : o.termin_tygodni ? tygodnie(o.termin_tygodni) : (o.termin_montazu || null),
    gwarancja: o.gwarancja_lat != null ? lata(o.gwarancja_lat) : null,
    wiadomosc: o.wiadomosc || '',
    // Znacznik budżetu i zamienniki liczy backend (wariant D): `w_budzecie` null, gdy klient nie podał budżetu.
    wBudzecie: o.w_budzecie ?? null,
    zamienniki: o.zamienniki || [],
    maOferte: Boolean(u.oferta),
    stan: u.stan,
    kontakt: Boolean(u.kontakt) || u.stan === 'przeszla' || u.stan === 'wybrana',
    koniec: Boolean(u.koniec_kontaktu),
  }
}

/** Przykład z `config/dlaDomu` na kartę. */
export const kartaZPrzykladu = (w) => ({
  id: w.id,
  nazwa: w.nazwa,
  logo: w.logo,
  certyfikat: Boolean(w.certyfikat),
  miasto: w.miasto,
  odleglosc: w.odleglosc,
  google: w.google,
  zdjecia: w.zdjecia.map(zdjecie),
  wiecej: w.wiecej,
  powody: w.powody,
  specjalizacje: w.specjalizacje,
  fakty: w.fakty,
  cena: w.cena,
  termin: tygodnie(w.termin),
  gwarancja: lata(w.gwarancja),
  wiadomosc: '',
  maOferte: true,
  stan: 'potwierdzona',
  kontakt: false,
  koniec: false,
})

/** Najniższa cena wśród ofert (kilka równych też na niebiesko). */
export const najnizszaCena = (karty) => {
  const ceny = karty.map((k) => k.cena).filter((c) => c != null)
  return ceny.length ? Math.min(...ceny) : null
}
